import { type ContentImage, imageTransformRuntimeOptions, resizedAssetUrl } from "@playbrain/content-types"

/**
 * Cover images for the archive ported off Squarespace.
 *
 * The seeded records carry no CMS asset: those are per-stage S3 objects and the
 * migration that created the records cannot upload one. The covers are shipped
 * as static files instead, so the news cards and the home strip look like the
 * live site rather than showing an empty frame.
 *
 * Keyed by article rather than by locale, matching how the records are grouped:
 * five of the six articles use the same cover in both languages, and the sixth
 * exists in Japanese only.
 *
 * This is a bridge, not the model. `newsCoverImage` on the record always wins,
 * so the first editor to upload a real cover replaces the static one for good.
 */
const coversByArticle: Record<string, string> = {
  "2023-esports-report": "/images/playdata/news/2023-esports-report.jpg",
  "2023q2": "/images/playdata/news/2023q2.jpg",
  "2023q3": "/images/playdata/news/2023q3.jpg",
  "2024-esports-report": "/images/playdata/news/2024-esports-report.jpg",
  "2025-report": "/images/playdata/news/2025-report.jpg",
  release: "/images/playdata/news/release.jpg",
}

/** The 2025 report is the one article whose slug differs between locales. */
const articleKeyBySlug: Record<string, string> = {
  "2025-japan-esports-report-released": "2025-report",
  "2025e": "2025-report",
}

export function playdataNewsCover(slug: string | null | undefined): string | undefined {
  if (!slug) return undefined
  return coversByArticle[articleKeyBySlug[slug] ?? slug]
}

/**
 * The image a card should draw: the record's own cover when an editor has set
 * one, otherwise the ported file.
 */
export function playdataNewsCoverSrc(
  slug: string | null | undefined,
  image: ContentImage | null | undefined
): string | undefined {
  const fromRecord = resizedAssetUrl(image?.asset, "markdown", {
    ...imageTransformRuntimeOptions(import.meta.env),
  })
  return fromRecord ?? playdataNewsCover(slug)
}
